<template>
  <div class="is-inline-block">
    <div v-if="currentDeal && !deliveryChecklistControlActive">
      <div :id="instanceId" :name="instanceId" class="quick-action">
        <button type="button" class="button" @click.stop.prevent="openModal(false)" v-tooltip="'Ferrari delivery checklist'">
          <i data-type="D" class="icon fal fa-clipboard-check fa-lg" :class="getColorClassByAgreementStatus(currentDeal.deliveryChecklistStatusId)"/>
        </button>
      </div>
      <b-dropdown
        ref="actionDropdown"
        aria-role="list"
        class="delivery-checklist-control"
        v-if="hasSignedCopies"
        :scrollable="true"
        :close-on-click="false"
        :mobile-modal="true"
        :expanded="true"
        :autoClose="true">
          <b-dropdown-item aria-role="listitem" @click="openModal(true)">Open</b-dropdown-item>
          <b-dropdown-item aria-role="listitem" @click="onDownloadSignedCopies">Download Last Signed</b-dropdown-item>
      </b-dropdown>
    </div>

    <portal to="global-modal-portal" :order="1" v-else>
      <b-modal :active.sync="deliveryChecklistControlActive" scroll="keep" :has-modal-card="true" :canCancel="false" class="delivery-checklist-modal">
        <div class="modal-card">
          <header class="modal-card-head">
            <p class="modal-card-title">Vehicle final check and delivery checklist for {{ inventoryItem ? inventoryItem.stockNo : '' }}</p>
          </header>
          <section class="modal-card-body view-container" style="padding:12px" v-if="!isLoading">
            <error-display-component :serverErrors="serverErrors" :serverErrorMessage="serverErrorMessage"></error-display-component>
            <b-tabs size="is-small" :animated="false" type="is-toggle" v-model="tabIndex">
              <b-tab-item label="Checklist" icon-pack="fas" icon="fas fa-clipboard-check fa-sm">
                <div class="columns is-multiline is-mobile delivery-header-fields">
                  <div class="column is-12">
                    <b-switch v-model="customerTypedSignature" type="is-info" :disabled="isSaving || !!ipadLink">
                      <span v-tooltip="'When switched on you are signing on behalf of the customer, /S/' + purchaserDisplayName + ' is typed on the customer signature lines'">Digital customer signature</span>
                    </b-switch>
                  </div>
                  <div class="column is-3">
                    <div class="field">
                      <label class="label">Registration Number</label>
                      <div class="control">
                        <input name="registrationNumber" class="input" type="text" maxlength="20" v-model="registrationNumber">
                      </div>
                    </div>
                  </div>
                  <div class="column is-3">
                    <div class="field">
                      <label class="label">Mileage<b-icon pack="fas" icon="star" class="fa-ss" v-if="mileageRequired"></b-icon></label>
                      <div class="control">
                        <input name="mileage" class="input" :class="{'is-danger': mileageRequired && !mileageValid}" type="number" min="0" step="1" v-model="mileage">
                      </div>
                      <span v-show="mileageRequired && !mileageValid" class="help is-danger"><i class="fas fa-exclamation-triangle"></i> Mileage is required</span>
                      <span v-if="vehicleMileage !== null" class="help"><a class="has-text-success" @click.prevent="mileage = vehicleMileage">{{ vehicleMileage.toLocaleString() }}</a></span>
                    </div>
                  </div>
                  <div class="column is-3">
                    <div class="field">
                      <label class="label">Delivery Date<b-icon pack="fas" icon="star" class="fa-ss" v-if="includeChecklist"></b-icon></label>
                      <div class="control delivery-date-control">
                        <el-date-picker
                          v-model="deliveryDate"
                          type="date"
                          placeholder="Click to select..."
                          popper-class="datepicker-container"
                          :clearable="true"
                          :editable="false"
                          format="MM-dd-yyyy">
                        </el-date-picker>
                      </div>
                      <span v-show="includeChecklist && !deliveryDate" class="help is-danger"><i class="fas fa-exclamation-triangle"></i> Delivery date is required</span>
                    </div>
                  </div>
                  <div class="column is-3">
                    <div class="field">
                      <label class="label">Time</label>
                      <div class="control">
                        <input name="deliveryTime" class="input" type="time" v-model="deliveryTime">
                      </div>
                    </div>
                  </div>
                  <div class="column is-12">
                    <button type="button" class="button is-small is-info" @click="onTickAll()" :disabled="allChecked">Tick all</button>
                    <span class="help is-inline-block ml-10">{{ checkedCount }} of {{ checklistItems.length }} items ticked</span>
                  </div>
                  <div class="column is-12" v-for="section in checklistSections" :key="section.title">
                    <p class="checklist-section-title">{{ section.title }}</p>
                    <div class="columns is-mobile is-vcentered checklist-item" v-for="item in section.items" :key="'CI' + item.number">
                      <div class="column is-7">
                        <b-checkbox v-model="item.checked" size="is-small">{{ item.number }}. {{ item.text }}</b-checkbox>
                      </div>
                      <div class="column is-5">
                        <input :name="'comment' + item.number" class="input is-small" type="text" maxlength="80" placeholder="Comment (optional)" v-model="item.comment">
                      </div>
                    </div>
                  </div>
                </div>
              </b-tab-item>
              <b-tab-item label="Power Contract" icon-pack="fas" icon="fas fa-file-contract fa-sm">
                <div class="columns is-multiline is-mobile" v-if="!activePowerContract">
                  <div class="column is-12">
                    <p class="help mb-10">Optional, the checklist can be signed or sent without it. Upload the Power Contract PDF downloaded from the Ferrari portal, the signature fields are placed automatically, check them and click Next to save them.</p>
                    <file-selector
                      v-model="powerContractFiles"
                      label="Power Contract (PDF from the Ferrari portal)"
                      :multiple="false"
                      :required="false"
                      :isBoxed="false"
                      :usePortalViewer="false">
                    </file-selector>
                  </div>
                  <div class="column is-12">
                    <button type="button" class="button is-primary" @click="onUploadPowerContract()" :class="{'is-loading': isPowerContractBusy && powerContractAction === 'upload'}" :disabled="isPowerContractBusy || !uploadedPowerContractFile">Upload and place fields</button>
                    <span class="help is-danger" v-if="hasNonPdfPowerContractFile"><i class="fas fa-exclamation-triangle"></i> The Power Contract must be a PDF file</span>
                  </div>
                </div>
                <div v-else>
                  <div class="is-flex power-contract-status mb-10">
                    <span class="tag is-success" v-if="powerContractConfirmed">Placements saved</span>
                    <span class="tag is-warning" v-else>Check the placements then click Next to save them</span>
                    <span class="help is-inline-block ml-10">{{ powerContractFields.length }} field(s), {{ powerContractSignatureCount }} customer signature(s)</span>
                    <a class="ml-10" target="live-doc-download" :href="buildDownloadUrl(activePowerContract.fileLocation)" title="Download the uploaded Power Contract"><i class="icon fal fa-cloud-download fa-lg has-text-link"/></a>
                  </div>
                  <div class="mb-10">
                    <button type="button" class="button is-small is-danger" @click="confirmReplacePowerContract = true" :disabled="isPowerContractBusy">Replace contract</button>
                  </div>
                  <div class="notification is-warning" v-if="confirmReplacePowerContract">
                    <p class="mb-10">Remove the current Power Contract and its field placements so a new one can be uploaded?</p>
                    <button type="button" class="button is-small is-danger mr-5" @click="onRemovePowerContract()" :class="{'is-loading': isPowerContractBusy && powerContractAction === 'remove'}" :disabled="isPowerContractBusy">Yes, remove it</button>
                    <button type="button" class="button is-small" @click="confirmReplacePowerContract = false" :disabled="isPowerContractBusy">Keep it</button>
                  </div>
                  <div class="columns is-multiline is-mobile delivery-header-fields">
                    <div class="column is-3" v-if="contractStartRequired">
                      <div class="field">
                        <label class="label">Contract Start Date<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control delivery-date-control">
                          <el-date-picker
                            v-model="contractStartDate"
                            type="date"
                            placeholder="Click to select..."
                            popper-class="datepicker-container"
                            :clearable="true"
                            :editable="false"
                            format="MM-dd-yyyy">
                          </el-date-picker>
                        </div>
                      </div>
                    </div>
                    <div class="column is-3" v-if="contractEndRequired">
                      <div class="field">
                        <label class="label">Contract End Date<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control delivery-date-control">
                          <el-date-picker
                            v-model="contractEndDate"
                            type="date"
                            placeholder="Click to select..."
                            popper-class="datepicker-container"
                            :clearable="true"
                            :editable="false"
                            format="MM-dd-yyyy">
                          </el-date-picker>
                        </div>
                        <div class="is-flex" v-if="contractStartDate">
                          <pop-fill-control
                            v-for="years in [1, 2]"
                            :key="'CE' + years"
                            class="mr-10"
                            customClass="popfill-link-date"
                            :title="'Click to set the end date ' + years + ' year' + (years > 1 ? 's' : '') + ' after the start date'"
                            :fillableValue="years + 'yr'"
                            :onFillAction="() => onFillContractEndDate(years)">
                          </pop-fill-control>
                        </div>
                      </div>
                    </div>
                    <div class="column" :class="{'is-6': contractStartRequired || contractEndRequired, 'is-12': !contractStartRequired && !contractEndRequired}">
                      <span class="help" v-if="powerContractPrefilledNames.length > 0">Already printed on the contract, left as printed: {{ powerContractPrefilledNames.join(', ') }}.</span>
                      <span class="help">Mileage, start and end date are typed onto the contract only where it left them blank.</span>
                      <span class="help is-danger" v-if="!contractDatesValid"><i class="fas fa-exclamation-triangle"></i> {{ contractDatesHint }}</span>
                    </div>
                  </div>
                  <power-contract-placement-editor
                    v-if="powerContractTabVisited"
                    v-model="powerContractFields"
                    :pdfUrl="buildDownloadUrl(activePowerContract.fileLocation)"
                    :pages="powerContractPlacements.pages"
                    :missing="powerContractPlacements.missing"
                    :readOnly="isPowerContractBusy"
                    :autoScroll="!contractStartRequired && !contractEndRequired"
                    @input="onPowerContractFieldsChanged">
                  </power-contract-placement-editor>
                </div>
              </b-tab-item>
              <b-tab-item label="Send" icon-pack="fas" icon="fas fa-paper-plane fa-sm">
                <div class="columns is-multiline is-mobile">
                  <div class="column is-12">
                    <label class="label">Include</label>
                    <b-checkbox :value="packIncludesContract" @input="includePowerContract = $event" :disabled="isSaving || !!ipadLink || !activePowerContract">Power Contract{{ activePowerContract ? '' : ' (none uploaded)' }}</b-checkbox>
                    <b-checkbox v-model="includeChecklist" :disabled="isSaving || !!ipadLink" class="ml-10">Delivery checklist</b-checkbox>
                  </div>
                  <div class="column is-12" v-if="customerTypedSignature && !confirmTypedSignature">
                    <p class="help">Digital customer signature is on (Checklist tab), click Sign with /S/ to sign on behalf of {{ purchaserDisplayName || 'the customer' }}.</p>
                  </div>
                  <div class="column is-12" v-if="confirmTypedSignature">
                    <div class="notification is-warning">
                      <p class="mb-10">You are signing on behalf of the customer. This places "/S/{{ purchaserDisplayName }}" and today's date on the customer signature lines of the {{ packTitle }}, and files it as signed. Continue?</p>
                      <button type="button" class="button is-small is-primary mr-5" @click="onSignTyped()" :class="{'is-loading': isSaving && pendingAction === 'sign'}" :disabled="isSaving">Yes, sign with /S/</button>
                      <button type="button" class="button is-small" @click="confirmTypedSignature = false" :disabled="isSaving">No</button>
                    </div>
                  </div>
                  <div class="column is-12" v-if="ipadLink">
                    <signing-link-panel
                      :link="ipadLink"
                      :defaultMobile="(currentDeal && currentDeal.purchaserContactMobile) || ''"
                      :sms="{ dealId: currentDeal.id, customerId: currentDeal.purchaserId, content: packTitle + ' for ' + vehicleName + ', open this link to sign' }"
                      :coBuyer="currentDeal.coCustomerId ? { id: currentDeal.coCustomerId, name: currentDeal.coCustomerName, mobile: currentDeal.coCustomerMobile } : null"
                      v-on:close="onCloseIpadLink">
                    </signing-link-panel>
                  </div>
                  <div class="column is-12">
                    <customer-selector
                      label="Customer"
                      validateAs="customer"
                      class="is-fullwidth"
                      v-model="selectedRecipientCustomer"
                      :selectedDealer="selectedDealer"
                      :required="true"
                      :showNewAction="false"
                      :useValidator="thisValidator"
                      data-vv-scope="recipient-form"
                      :isDisabled="true"
                      :allowEdit="false">
                    </customer-selector>
                  </div>
                  <div class="column is-12" v-if="recipientSelected && !customerTypedSignature">
                    <div class="field">
                      <label class="label">Recipients<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                      <multiselect
                        :multiple="true"
                        :taggable="true"
                        v-validate="{'required': true}"
                        data-vv-scope="recipient-form"
                        data-vv-as="recipient"
                        data-vv-validate-on="input|close"
                        :class="{'is-danger': errors.has('recipient-form.recipients') }"
                        @tag="onRecipientAdded"
                        name="recipients"
                        label="fullName"
                        trackBy="email"
                        tagPlaceholder="Enter to add email address"
                        v-model="selectedRecipients"
                        :options="selectedRecipients"
                        :disabled="false">
                      </multiselect>
                      <span v-show="errors.has('recipient-form.recipients')" class="help is-danger"><i v-show="errors.has('recipient-form.recipients')" class="fas fa-exclamation-triangle"></i> {{ errors.first('recipient-form.recipients') }}</span>
                    </div>
                  </div>
                  <div class="column is-12" v-if="recipientSelected && !customerTypedSignature">
                    <div class="field">
                      <label class="label">Subject</label>
                      <div class="control">
                        <input name="overridingSubject" data-vv-as="overridingSubject" :class="{'input': true }" type="text" v-model="overridingSubject">
                      </div>
                    </div>
                  </div>
                  <div class="column is-12" v-if="recipientSelected && !customerTypedSignature">
                    <div class="field">
                      <label class="label">Message (optional)</label>
                      <div class="control">
                        <textarea rows="10" name="optionalMessage" :class="{'textarea': true}" data-vv-as="optional message" v-model="optionalMessage"></textarea>
                      </div>
                    </div>
                  </div>
                </div>
              </b-tab-item>
              <b-tab-item label="History" icon-pack="fas" icon="fas fa-history fa-sm">
                <div class="mb-10" v-if="hasSignedCopies">
                  <span class="label is-inline-block mr-10">Signed copies</span>
                  <a class="button is-small mr-5" target="live-doc-download" :href="buildDownloadUrl(signedServiceContract.fileLocation)" v-if="signedServiceContract"><i class="fal fa-cloud-download mr-5"/>Service Contract</a>
                  <a class="button is-small mr-5" target="live-doc-download" :href="buildDownloadUrl(signedChecklist.fileLocation)" v-if="signedChecklist"><i class="fal fa-cloud-download mr-5"/>Delivery Checklist</a>
                  <a class="button is-small is-primary" target="live-doc-download" :href="signedCopiesUrl" v-if="signedServiceContract && signedChecklist"><i class="fal fa-cloud-download mr-5"/>Download both</a>
                </div>
                <section>
                  <b-table
                    class="lists"
                    :data="deliveryChecklistSendHistory">
                    <template slot-scope="props">
                      <b-table-column field="status" label="Status" sortable :class="{'striked': props.row.delete === true && props.row.id > 0}">
                        <i data-type="D" class="icon fal fa-clipboard-check fa-lg list-icon" :class="getColorClassByAgreementStatus(props.row.signingStatus)" :title="'Delivery Checklist Status: ' + getDescriptionByAgreementStatus(props.row.signingStatus)"/>
                        <a target="live-doc-download" :href="buildDownloadUrl(props.row.serviceContractFileLocation)" title="Click to download the signed service contract." v-if="props.row.serviceContractFileLocation && (props.row.signingStatus == 10 || props.row.signingStatus === 3)"><i class="icon fal fa-file-contract fa-lg list-icon has-text-success"/></a>
                        <a target="live-doc-download" :href="buildDownloadUrl(props.row.dealFileLocation)" title="Click to download the last signed delivery checklist." v-if="props.row.dealFileLocation && (props.row.signingStatus == 10 || props.row.signingStatus === 3)"><i class="icon fal fa-cloud-download fa-lg list-icon has-text-success"/></a>
                      </b-table-column>
                      <b-table-column field="email" label="Recipients" sortable :class="{'striked': props.row.delete === true && props.row.id > 0}">
                        {{ props.row.destinationAddresses }}
                      </b-table-column>
                      <b-table-column field="date" label="Date" sortable :class="{'striked': props.row.delete === true && props.row.id > 0}">
                        {{ props.row.sentAt | formatDateOnly }}
                      </b-table-column>
                      <b-table-column field="time" label="Time" sortable :class="{'striked': props.row.delete === true && props.row.id > 0}">
                        {{ convertTimeString(props.row.sentAt, props.row.signingStatus) }}
                      </b-table-column>
                      <b-table-column field="id" label="Actions">
                        <a v-if="props.row.cancellationSent === false" @click.stop.prevent="onCancelSignatureRequest(props.row)"><i class="icon fa-fw fal fa-times-circle fa-lg" :class="{'has-text-danger': !isCancellationDisabled(props.row), 'has-text-grey': isCancellationDisabled(props.row)}" :title="isCancellationDisabled(props.row) ? 'Request cannot be cancelled at this point' : 'Cancel Request'"/></a>
                        <a v-else><i class="icon fa-fw fal fa-times-circle fa-lg has-text-success" title='Cancellation request has been made'/></a>
                        <a v-if="props.row.reminderSent === false" @click.stop.prevent="onSignatureRequestReminder(props.row)"><i class="icon fa-fw fal fa-redo fa-lg" :class="{'has-text-link': !isReminderDisabled(props.row), 'has-text-grey': isReminderDisabled(props.row)}" :title="isReminderDisabled(props.row) ? 'Reminder cannot be sent at this point' : 'Send Reminder'"/></a>
                        <a v-else><i class="icon fa-fw fal fa-envelope fa-lg has-text-success" title='Reminder has been sent'/></a>
                      </b-table-column>
                    </template>

                    <template slot="empty">
                      <section class="section">
                        <div class="has-text-centered">
                          <p><b-icon icon="emoticon-sad" size="is-large"></b-icon></p>
                          <p>No delivery checklist documents have been sent for this deal.</p>
                        </div>
                      </section>
                    </template>

                  </b-table>
                </section>
              </b-tab-item>
            </b-tabs>
          </section>
          <section class="modal-card-body" v-else>
            <b-loading :is-full-page="false" :active.sync="isLoading" :canCancel="false"></b-loading>
          </section>
          <footer class="modal-card-foot">
            <button type="button" class="button" @click="tabIndex = tabIndex - 1" :disabled="isSaving || isPowerContractBusy" v-if="tabIndex > 0 && tabIndex <= 2">Previous</button>
            <button type="button" class="button is-primary" @click="onNext()" :class="{'is-loading': (isSaving && pendingAction === 'next') || (isPowerContractBusy && powerContractAction === 'confirm')}" :disabled="isSaving || isPowerContractBusy" v-if="tabIndex < 2">Next</button>
            <button type="button" class="button is-primary" @click="confirmTypedSignature = true" :disabled="isSaving || isPowerContractBusy || !canSignOrSend" :title="canSignOrSend ? 'Sign for the customer with a typed /S/ signature' : incompleteHint" v-if="tabIndex === 2 && customerTypedSignature">Sign with /S/</button>
            <button type="button" class="button is-primary" @click="onSignNow()" :class="{'is-loading': isSaving && pendingAction === 'sign'}" :disabled="isSaving || isPowerContractBusy || !canSignOrSend" :title="canSignOrSend ? 'Save and capture the customer signature' : incompleteHint" v-if="tabIndex === 2 && !customerTypedSignature">Sign</button>
            <button type="button" class="button is-primary" @click="onSignOnIpad()" :class="{'is-loading': isSaving && pendingAction === 'ipad'}" :disabled="isSaving || isPowerContractBusy || !canSignOrSend" :title="canSignOrSend ? 'Create a link that opens the signature pad on an iPad' : incompleteHint" v-if="tabIndex === 2 && !customerTypedSignature">Sign on iPad</button>
            <button type="button" class="button is-primary" @click="onValidateSend()" :class="{'is-loading': isSaving && pendingAction === 'send'}" :disabled="isSaving || isPowerContractBusy || !canSignOrSend || !formDataValid" v-if="tabIndex === 2 && !customerTypedSignature">Send</button>
            <button type="button" class="button is-danger" @click="onCancel()">Cancel</button>
            <span class="help is-danger" v-if="tabIndex === 2 && !canSignOrSend">{{ incompleteHint }}</span>
            <!-- Garrett: kept apart from the other buttons -->
            <button type="button" class="button is-primary footer-preview" @click="onPreview()" :class="{'is-loading': isSaving && pendingAction === 'preview'}" :disabled="isSaving" :title="previewTitle">Preview</button>
          </footer>
        </div>
      </b-modal>
    </portal>

    <portal to="global-modal-portal" v-if="isSignatureActive" :order="3">
      <b-modal :key="'DCS' + instanceId" :active.sync="isSignatureActive" scroll="keep" :has-modal-card="true" :canCancel="false">
        <div class="modal-card">
          <header class="modal-card-head">
            <p class="modal-card-title">Customer signature - {{ purchaserDisplayName }} — signs the {{ packTitle }}</p>
          </header>
          <section class="modal-sign-body">
            <vue-signature ref="signature" :sigOption="option" :w="'500px'" :h="'150px'"></vue-signature>
          </section>
          <footer class="modal-card-foot">
            <button type="button" class="button is-primary" :class="{'is-loading': loadingSign }" @click.prevent="onSign">Accept</button>
            <button type="button" class="button is-warning" @click.prevent="onClearSign">Clear</button>
            <button type="button" class="button is-danger" @click.prevent="onCancelSignNow">Cancel</button>
          </footer>
        </div>
      </b-modal>
    </portal>

    <pdf-modal
      v-if="deliveryChecklistDocument && deliveryChecklistDocument.src"
      :pdfFile="deliveryChecklistDocument"
      :usePortal="true"
      :portalOrder="2"
      v-on:on-pdf-closed="onPreviewClosed"
      :fullScreenMode="true">
    </pdf-modal>

    <send-reminder-control
      v-on:on-hide-reminder-control="onHideReminderModal"
      :showConfirmationModal="showConfirmReminder"
      :signatureRequest="signatureRequest"
      v-on:send-reminder="sendReminder">
    </send-reminder-control>

    <cancel-signature-request-control
      v-on:on-hide-cancel-request-control="onHideCancellationRequestModal"
      :showRequestCancellationModal="showCancelRequest"
      :signatureRequest="cancelSignatureRequest"
      v-on:send-cancellation-request="sendCancellationRequest">
    </cancel-signature-request-control>
  </div>
</template>

<script>
import moment from 'moment'
import eventBus from '@/eventBus'
import vueSignature from 'vue-signature'
import ErrorDisplayComponent from '@/components/generic/ErrorDisplayComponent'
import { createNamespacedHelpers } from 'vuex'
import dealService from '@/services/dealService'
import documentSigningRequestService from '@/services/documentSigningRequestService'
import documentSigningLinkService from '@/services/documentSigningLinkService'
import formattingMixin from '@/mixins/inventory/formatting'
import utilitiesMixin from '@/mixins/generic/utilities'
import validateBeforeSubmitMixin from '@/mixins/generic/validateBeforeSubmit'
import CustomerSelector from '@/components/generic/CustomerSelector'
import SendReminderControl from '@/components/generic/SendReminderControl'
import CancelSignatureRequestControl from '@/components/generic/CancelSignatureRequestControl'
import PdfModalDisplayComponent from '@/components/generic/PdfModalDisplayComponent'
import FileSelector from '@/components/generic/FileSelector'
import PowerContractPlacementEditor from '@/components/sales/deals/PowerContractPlacementEditor'
import SigningLinkPanel from '@/components/generic/signing/SigningLinkPanel'
import PopFillControl from '@/components/generic/PopFillControl'
const mapDealerState = createNamespacedHelpers('dealer').mapState
const mapUserGetters = createNamespacedHelpers('user').mapGetters

// item texts as printed on the Ferrari "Vehicle Final Check and Delivery Checklist" form
const DELIVERY_CHECKLIST_ITEMS = [
  { number: 1, text: 'DELIVERY TO CUSTOMER WITH FUNCTIONAL TEST OF KEYS' },
  { number: 2, text: 'FITTING AND CHECKING OF NUMBER PLATES' },
  { number: 3, text: 'CHECK THAT ALL MANUALS AND PART NUMBER LISTS ARE IN VEHICLE' },
  { number: 4, text: 'FUNCTIONING OF INFOTAINMENT SYSTEM AND PRE-TUNING' },
  { number: 5, text: 'RADIO STATIONS AS REQUESTED BY CUSTOMER' },
  { number: 6, text: 'CHECK FOR TOOL KIT' },
  { number: 7, text: 'CHECK THERE IS A FULL TANK OF FUEL' },
  { number: 8, text: 'CHECK VEHICLE IS CLEAN (TAKE ACTION IF NECESSARY)' },
  { number: 9, text: 'CHECK FULL CHARGE LEVEL OF BATTERY' },
  { number: 10, text: 'EXPLAIN WARRANTY TERMS AND CONDITIONS' },
  { number: 11, text: 'EXPLAIN TERMS AND CONDITIONS OF ROADSIDE ASSISTANCE' },
  { number: 12, text: 'EXPLAIN SCHEDULING OF SERVICING' },
  { number: 13, text: 'PRESENTATION TO AFTERSALES DEPARTMENT/LISTING OF PERSONNEL TO CONTACT' },
  { number: 14, text: 'PRESENTATION OF VEHICLE AND CHECKING OF CONDITIONS BY CUSTOMER' },
  { number: 15, text: 'EXPLAIN LITERATURE PACK' },
  { number: 16, text: 'COMPLETION AND SIGNING OF ALL DOCUMENTATION' },
  { number: 17, text: 'HANDING OVER OF KEYS AND EXPLANATION OF HOW ANTI-THEFT SYSTEM OPERATES' },
  { number: 18, text: 'PUTTING VEHICLE IN KEY-ON 30 SECONDS BEFORE STARTING THE ENGINE' },
  { number: 19, text: 'DEMO AND EXPLANATION OF VEHICLE CONTROLS' },
  { number: 20, text: 'DEMO OF ICE SYSTEM, SAT NAV SYSTEM AND OTHER SETTINGS ACCORDING TO CUSTOMER\'S LIST OF PREFERENCES' },
  { number: 21, text: 'TEST DRIVE PROPOSED TO GET TO KNOW VEHICLE' },
  { number: 22, text: 'HAVE YOU BEEN DEALT WITH PROFESSIONALLY AND COMPETENTLY?' },
  { number: 23, text: 'HAVE YOU BEEN TREATED POLITELY?' },
  { number: 24, text: 'ARE YOU SATISFIED WITH THE TIME REQUIRED FOR VEHICLE DELIVERY?' }
]

const DELIVERY_CHECKLIST_SECTIONS = [
  { title: 'PRE-DELIVERY AND FINAL CHECK BY SALES EXECUTIVE', from: 1, to: 9 },
  { title: 'DELIVERY PROCEDURE AND CHECKLIST', from: 10, to: 21 },
  { title: 'QUESTIONS ON GENERAL LEVEL OF CUSTOMER SATISFACTION', from: 22, to: 24 }
]

const POWER_CONTRACT_PREFILLED_NAMES = { mileage: 'Mileage', startDate: 'Start date', endDate: 'End date' }

export default {
  mixins: [validateBeforeSubmitMixin, formattingMixin, utilitiesMixin],
  components: {
    'error-display-component': ErrorDisplayComponent,
    'customer-selector': CustomerSelector,
    'send-reminder-control': SendReminderControl,
    'cancel-signature-request-control': CancelSignatureRequestControl,
    'pdf-modal': PdfModalDisplayComponent,
    'file-selector': FileSelector,
    'power-contract-placement-editor': PowerContractPlacementEditor,
    'vue-signature': vueSignature,
    'signing-link-panel': SigningLinkPanel,
    'pop-fill-control': PopFillControl
  },
  name: 'DealDeliveryChecklistAction',
  props: {
    currentDeal: {
      type: Object,
      default: null
    }
  },
  data () {
    return {
      isSaving: false,
      isLoading: false,
      pendingAction: '',
      hasSavedChanges: false,
      instanceId: this.$uuid.v4(),
      serverErrors: [],
      serverErrorMessage: '',
      deliveryChecklistControlActive: false,
      selectedRecipientCustomer: null,
      selectedDealer: null,
      selectedRecipients: [],
      selectedRecipientsMobile: [],
      optionalMessage: '',
      overridingSubject: '',
      deliveryChecklistDocument: null,
      inventoryItem: null,
      tabIndex: 0,
      previewViewed: false,
      registrationNumber: '',
      mileage: null,
      deliveryDate: null,
      deliveryTime: '',
      contractStartDate: null,
      contractEndDate: null,
      includeChecklist: true,
      includePowerContract: true,
      customerTypedSignature: false,
      confirmTypedSignature: false,
      ipadLink: null,
      checklistItems: [],
      isSignatureActive: false,
      loadingSign: false,
      option: {
        penColor: 'rgb(0, 0, 0)',
        backgroundColor: 'rgb(255,255,255)'
      },
      deliveryChecklistSendHistory: [],
      perPage: 100,
      page: 1,
      total: 0,
      signatureRequest: null,
      showConfirmReminder: false,
      sendingReminder: false,
      cancelSignatureRequest: null,
      showCancelRequest: false,
      sendingCancellationRequest: false,
      powerContractDeal: null,
      powerContractFiles: [],
      powerContractFields: [],
      powerContractLocalDirty: false,
      powerContractTabVisited: false,
      isPowerContractBusy: false,
      powerContractAction: '',
      confirmReplacePowerContract: false
    }
  },
  computed: {
    ...mapUserGetters(['currentUser']),
    ...mapDealerState(['currentDealer']),
    checklistSections: function () {
      return DELIVERY_CHECKLIST_SECTIONS.map((section) => {
        return {
          title: section.title,
          items: this.checklistItems.filter((x) => x.number >= section.from && x.number <= section.to)
        }
      })
    },
    checkedCount: function () {
      return this.checklistItems.filter((x) => x.checked).length
    },
    allChecked: function () {
      return this.checklistItems.length > 0 && this.checkedCount === this.checklistItems.length
    },
    vehicleMileage: function () {
      return (this.inventoryItem && this.inventoryItem.usage !== null && this.inventoryItem.usage !== undefined) ? this.inventoryItem.usage : null
    },
    mileageValid: function () {
      let parsedMileage = parseInt(this.mileage, 10)
      return !isNaN(parsedMileage) && parsedMileage >= 0
    },
    // the contract only needs it when the printout left its mileage blank for us to type on
    contractMileageRequired: function () {
      return this.packIncludesContract && this.powerContractFields.some((x) => x.type === 'mileage')
    },
    mileageRequired: function () {
      return this.includeChecklist || this.contractMileageRequired
    },
    headerFieldsValid: function () {
      return (!this.mileageRequired || this.mileageValid) && (!this.includeChecklist || !!this.deliveryDate)
    },
    // Garrett 10-21: the checklist and the contract can each be signed or sent alone, or together
    packIncludesContract: function () {
      return this.includePowerContract && !!this.activePowerContract
    },
    packTitle: function () {
      if (this.includeChecklist && this.packIncludesContract) return 'Power Contract and Delivery Checklist'
      return this.packIncludesContract ? 'Power Contract' : 'Delivery Checklist'
    },
    purchaserDisplayName: function () {
      return this.titleCaseName(this.currentDeal ? this.currentDeal.purchaserName : '')
    },
    previewTitle: function () {
      if (this.tabIndex === 0) return 'Save and preview the delivery checklist'
      if (this.tabIndex === 1) return 'Save and preview the Power Contract'
      return 'Save and preview the ' + this.packTitle + ' as it will be signed'
    },
    powerContractAttachments: function () {
      let deal = this.powerContractDeal || this.currentDeal
      return deal && deal.attachments ? deal.attachments : []
    },
    activePowerContract: function () {
      let allContracts = this.powerContractAttachments.filter((x) => x.classType === 'PowerContract' && x.status === 'Active')
      if (allContracts.length > 0) {
        return allContracts.reduce((a, b) => (b.id || 0) > (a.id || 0) ? b : a)
      }
      return null
    },
    powerContractPlacements: function () {
      let parsed = null
      if (this.activePowerContract && this.activePowerContract.description) {
        try {
          parsed = JSON.parse(this.activePowerContract.description)
        } catch (e) {
          parsed = null
        }
      }
      parsed = parsed || {}
      return {
        confirmed: parsed.confirmed === true,
        pages: Array.isArray(parsed.pages) ? parsed.pages : [],
        fields: Array.isArray(parsed.fields) ? parsed.fields : [],
        missing: Array.isArray(parsed.missing) ? parsed.missing : [],
        prefilled: Array.isArray(parsed.prefilled) ? parsed.prefilled : []
      }
    },
    powerContractPrefilledNames: function () {
      return this.powerContractPlacements.prefilled.map((x) => POWER_CONTRACT_PREFILLED_NAMES[x] || x)
    },
    // only asked for when the contract left the line blank, so a box is there to type it on
    contractStartRequired: function () {
      return !!this.activePowerContract && this.powerContractFields.some((x) => x.type === 'startDate')
    },
    contractEndRequired: function () {
      return !!this.activePowerContract && this.powerContractFields.some((x) => x.type === 'endDate')
    },
    contractDatesValid: function () {
      if (this.contractStartRequired && !this.contractStartDate) return false
      if (this.contractEndRequired && !this.contractEndDate) return false
      if (this.contractStartRequired && this.contractEndRequired) {
        return moment(this.contractEndDate).isAfter(moment(this.contractStartDate), 'day')
      }
      return true
    },
    contractDatesHint: function () {
      if ((this.contractStartRequired && !this.contractStartDate) || (this.contractEndRequired && !this.contractEndDate)) {
        return 'Contract start and end dates are required before signing or sending'
      }
      return 'The contract end date must be after its start date'
    },
    powerContractKey: function () {
      return this.activePowerContract ? this.activePowerContract.id + ':' + (this.activePowerContract.description || '') : ''
    },
    powerContractConfirmed: function () {
      return !!this.activePowerContract && this.powerContractPlacements.confirmed && !this.powerContractLocalDirty
    },
    powerContractSignatureCount: function () {
      return this.powerContractFields.filter((x) => x.type === 'signature').length
    },
    uploadedPowerContractFile: function () {
      return this.powerContractFiles.find((x) => x.isUploaded === true && !x.isUploadError && x.status !== 'Deleted' && !x.id && /\.pdf$/i.test(x.name || '')) || null
    },
    hasNonPdfPowerContractFile: function () {
      return this.powerContractFiles.some((x) => x.status !== 'Deleted' && !/\.pdf$/i.test(x.name || ''))
    },
    powerContractReady: function () {
      return !this.packIncludesContract || (this.powerContractConfirmed && this.contractDatesValid)
    },
    canSignOrSend: function () {
      return (this.includeChecklist || this.packIncludesContract) && this.headerFieldsValid && this.powerContractReady && this.previewViewed
    },
    missingHeaderFieldsHint: function () {
      let missingDetails = []
      if (this.mileageRequired && !this.mileageValid) missingDetails.push('mileage')
      if (this.includeChecklist && !this.deliveryDate) missingDetails.push('delivery date')
      return missingDetails.length > 0 ? 'enter the ' + missingDetails.join(', ') + ' on the Checklist tab' : ''
    },
    incompleteHint: function () {
      let outstanding = []
      if (!this.includeChecklist && !this.packIncludesContract) {
        outstanding.push('include the delivery checklist or the Power Contract')
      }
      if (this.missingHeaderFieldsHint) {
        outstanding.push(this.missingHeaderFieldsHint)
      }
      if (this.packIncludesContract && !this.powerContractConfirmed) {
        outstanding.push('save the Power Contract field placements with Next on the Power Contract tab')
      }
      if (this.packIncludesContract && !this.contractDatesValid) {
        outstanding.push('enter the contract start and end dates (end after start) on the Power Contract tab')
      }
      if (!this.previewViewed) {
        outstanding.push('view the preview on this tab')
      }
      return outstanding.length > 0 ? 'Before it can be signed or sent: ' + outstanding.join('; ') + '.' : ''
    },
    recipientSelected: function () {
      return this.currentDeal && this.currentDeal.purchaserName && this.selectedRecipientCustomer
    },
    formDataValid: function () {
      return this.selectedRecipientCustomer && this.selectedRecipients && this.selectedRecipients.length > 0
    },
    thisValidator: function () {
      return (this && this.$validator) ? this.$validator : null
    },
    // the contract and checklist are signed together but filed apart
    signedServiceContract: function () {
      return this.newestActiveAttachment('ServiceContract')
    },
    signedChecklist: function () {
      return this.newestActiveAttachment('DeliveryChecklist')
    },
    hasSignedCopies: function () {
      return !!this.signedServiceContract || !!this.signedChecklist
    },
    signedCopiesUrl: function () {
      return this.currentDeal ? process.env.VUE_APP_ROOT_DOC_SITE + 'docs/deals/delivery-pack/' + this.currentDeal.id : ''
    },
    vehicleName: function () {
      return this.inventoryItem ? this.inventoryItem.name : (this.currentDeal ? [this.currentDeal.vehicleProductionYear, this.currentDeal.vehicleMakeName, this.currentDeal.vehicleModelName].filter((x) => x).join(' ') : '')
    }
  },
  methods: {
    newestActiveAttachment: function (classType) {
      let attachments = (this.currentDeal && this.currentDeal.attachments) ? this.currentDeal.attachments.filter((x) => x.classType === classType && x.status === 'Active') : []
      return attachments.length > 0 ? attachments.reduce((a, b) => (b.id || 0) > (a.id || 0) ? b : a) : null
    },
    // Craig: both files stitched into one download
    onDownloadSignedCopies: function () {
      if (!this.hasSignedCopies) return
      window.location = this.signedCopiesUrl
    },
    buildDownloadUrl: function (fileLocation) {
      return process.env.VUE_APP_ROOT_API + 'document/download?url=' + encodeURI(fileLocation)
    },
    openModal (fromDropDown) {
      if (!fromDropDown && this.$refs.actionDropdown) {
        this.$refs.actionDropdown.toggle()
        return
      }
      this.serverErrors = []
      this.serverErrorMessage = ''
      this.tabIndex = 0
      this.previewViewed = false
      this.customerTypedSignature = false
      this.confirmTypedSignature = false
      this.ipadLink = null
      this.hasSavedChanges = false
      this.setInventoryItem()
      this.loadChecklist()
      this.selectedRecipients = []
      this.resetPowerContract()
      this.overridingSubject = this.packTitle + ' - ' + this.vehicleName
      this.optionalMessage = 'Please review and sign the attached ' + this.packTitle + ' for vehicle ' + this.vehicleName + '.'
      this.setDealRecipientInformation()
      this.deliveryChecklistControlActive = true
    },
    // the Deal Documents checklist opens the pack straight onto the Checklist (0) or Power Contract (1) tab
    openAtTab: function (tabIndex) {
      this.openModal(true)
      this.tabIndex = tabIndex
      this.powerContractTabVisited = tabIndex === 1
    },
    // Garrett 10-21: names show with the first letter of each name capitalised, however they were captured
    titleCaseName: function (name) {
      return (name || '').toLowerCase().replace(/(^|[\s'-])([a-z])/g, (match, separator, letter) => separator + letter.toUpperCase())
    },
    resetPowerContract: function () {
      this.powerContractDeal = null
      this.powerContractFiles = []
      this.powerContractTabVisited = false
      this.isPowerContractBusy = false
      this.powerContractAction = ''
      this.confirmReplacePowerContract = false
      this.initPowerContractFields()
    },
    initPowerContractFields: function () {
      this.powerContractFields = this.powerContractPlacements.fields.map((x) => Object.assign({}, x))
      this.powerContractLocalDirty = false
    },
    onPowerContractFieldsChanged: function () {
      // any edit after saving means the placements have to be saved (and confirmed) again
      this.powerContractLocalDirty = true
    },
    startPowerContractRequest: function (action) {
      this.serverErrors = []
      this.serverErrorMessage = ''
      this.powerContractAction = action
      this.isPowerContractBusy = true
    },
    applyPowerContractResponse: function (response, successMessage) {
      this.isPowerContractBusy = false
      this.powerContractAction = ''
      if (!response || response.result === false || response.errors) {
        this.failedToast('Oops! Something went wrong')
        this.serverErrors = (response && response.errors) || []
        this.serverErrorMessage = (response && response.message) || ''
        return false
      }
      if (response.deal) {
        this.powerContractDeal = response.deal
      }
      // the deal is refreshed once the modal is closed
      this.hasSavedChanges = true
      this.initPowerContractFields()
      this.successToast(successMessage)
      return true
    },
    onPowerContractRequestFailed: function (error) {
      this.isPowerContractBusy = false
      this.powerContractAction = ''
      this.failedToast('Oops! Something went wrong')
      this.serverErrorMessage = error && error.message ? error.message : ''
    },
    onUploadPowerContract: function () {
      let file = this.uploadedPowerContractFile
      if (!file) return
      let uploadModel = {
        dealId: this.currentDeal.id,
        uniqueId: file.uniqueId,
        uploadName: file.name,
        uploadSize: file.size
      }
      this.startPowerContractRequest('upload')
      dealService.uploadPowerContract(uploadModel).then(response => {
        if (this.applyPowerContractResponse(response, 'Success! Power Contract uploaded, please check the field placements then click Next.')) {
          this.powerContractFiles = []
        }
      }).catch((error) => {
        this.onPowerContractRequestFailed(error)
      })
    },
    onSavePowerContractPlacements: function () {
      let placementsModel = {
        dealId: this.currentDeal.id,
        confirmed: true,
        fields: this.powerContractFields.map((x) => {
          return { id: x.id, type: x.type, page: x.page, x: x.x, y: x.y, width: x.width, height: x.height, anchor: x.anchor || '', auto: x.auto === true }
        })
      }
      this.startPowerContractRequest('confirm')
      return dealService.savePowerContractPlacements(placementsModel).then(response => {
        let saved = this.applyPowerContractResponse(response, 'Success! Power Contract placements saved.')
        if (saved && this.ipadLink) {
          this.onIpadLinkInvalidated()
        }
        return saved
      }).catch((error) => {
        this.onPowerContractRequestFailed(error)
        return false
      })
    },
    onRemovePowerContract: function () {
      this.startPowerContractRequest('remove')
      dealService.removePowerContract({ dealId: this.currentDeal.id }).then(response => {
        if (this.applyPowerContractResponse(response, 'Success! Power Contract removed, a new one can now be uploaded.')) {
          this.confirmReplacePowerContract = false
          this.powerContractFiles = []
        }
      }).catch((error) => {
        this.onPowerContractRequestFailed(error)
      })
    },
    setInventoryItem: function () {
      this.inventoryItem = null
      if (this.currentDeal && this.currentDeal.items) {
        const soldItem = this.currentDeal.items.find((x) => x.dealItemType === 'Sold')
        if (soldItem && soldItem.forInventory) {
          this.inventoryItem = soldItem.forInventory
        }
      }
    },
    loadChecklist: function () {
      let saved = null
      if (this.currentDeal && this.currentDeal.deliveryChecklist) {
        try {
          saved = JSON.parse(this.currentDeal.deliveryChecklist)
        } catch (e) {
          saved = null
        }
      }

      if (saved) {
        let savedItems = Array.isArray(saved.items) ? saved.items : []
        this.registrationNumber = saved.registrationNumber || ''
        this.mileage = (saved.mileage !== null && saved.mileage !== undefined) ? saved.mileage : null
        this.deliveryDate = saved.deliveryDate ? moment(saved.deliveryDate, 'YYYY-MM-DD').toDate() : null
        this.deliveryTime = saved.deliveryTime || ''
        this.contractStartDate = saved.contractStartDate ? moment(saved.contractStartDate, 'YYYY-MM-DD').toDate() : null
        this.contractEndDate = saved.contractEndDate ? moment(saved.contractEndDate, 'YYYY-MM-DD').toDate() : null
        // saved before the choice existed means both
        this.includeChecklist = saved.includeChecklist !== false
        this.includePowerContract = saved.includePowerContract !== false
        this.checklistItems = DELIVERY_CHECKLIST_ITEMS.map((x) => {
          let savedItem = savedItems.find((y) => y.number === x.number)
          return {
            number: x.number,
            text: x.text,
            checked: savedItem ? savedItem.checked === true : false,
            comment: savedItem && savedItem.comment ? savedItem.comment : ''
          }
        })
      } else {
        // no checklist saved yet so prefill from the deal and the sold vehicle
        this.registrationNumber = ''
        this.mileage = this.vehicleMileage
        this.deliveryDate = this.getDealDeliveryDate()
        this.deliveryTime = moment().format('HH:mm')
        this.contractStartDate = null
        this.contractEndDate = null
        this.includeChecklist = true
        this.includePowerContract = true
        this.checklistItems = DELIVERY_CHECKLIST_ITEMS.map((x) => {
          return { number: x.number, text: x.text, checked: false, comment: '' }
        })
      }
    },
    getDealDeliveryDate: function () {
      let value = this.currentDeal ? this.currentDeal.deliveryDate : null
      if (!value) {
        return moment().startOf('day').toDate()
      }
      // ViewDeal converts deliveryDate to a local Date, a freshly returned deal still holds the utc string
      let localValue = value instanceof Date ? moment(value) : (moment.defaultZone ? moment.utc(String(value)).tz(moment.defaultZone.name) : moment.utc(String(value)).local())
      return moment(localValue.format('YYYY-MM-DD'), 'YYYY-MM-DD').toDate()
    },
    onTickAll: function () {
      this.checklistItems.forEach((x) => {
        x.checked = true
      })
    },
    buildChecklistModel: function () {
      let parsedMileage = parseInt(this.mileage, 10)
      return {
        dealId: this.currentDeal.id,
        registrationNumber: this.registrationNumber,
        mileage: isNaN(parsedMileage) ? null : parsedMileage,
        deliveryDate: this.deliveryDate ? moment(this.deliveryDate).format('YYYY-MM-DD') : null,
        deliveryTime: this.deliveryTime,
        contractStartDate: this.contractStartDate ? moment(this.contractStartDate).format('YYYY-MM-DD') : null,
        contractEndDate: this.contractEndDate ? moment(this.contractEndDate).format('YYYY-MM-DD') : null,
        includeChecklist: this.includeChecklist,
        includePowerContract: this.includePowerContract,
        items: this.checklistItems.map((x) => {
          return { number: x.number, checked: x.checked, comment: x.comment }
        })
      }
    },
    saveChecklist: function (action) {
      this.serverErrors = []
      this.serverErrorMessage = ''
      this.pendingAction = action
      this.isSaving = true
      let checklistModel = this.buildChecklistModel()
      return dealService.updateDeliveryChecklist(checklistModel).then(response => {
        this.isSaving = false
        if (response.result === false || response.errors) {
          this.failedToast('Oops! Something went wrong')
          this.serverErrors = response.errors || []
          this.serverErrorMessage = response.message || ''
          return false
        }
        this.hasSavedChanges = true
        if (this.ipadLink && JSON.stringify(checklistModel) !== this.ipadLink.checklistSnapshot) {
          this.onIpadLinkInvalidated()
        }
        return true
      }).catch((error) => {
        this.isSaving = false
        this.failedToast('Oops! Something went wrong')
        this.serverErrorMessage = error.message
        return false
      })
    },
    // Garrett: contracts are bought a year at a time, so the end date is most often one or two years on
    contractEndAfter: function (years) {
      return moment(this.contractStartDate).add(years, 'years')
    },
    onFillContractEndDate: function (years) {
      this.contractEndDate = this.contractEndAfter(years).toDate()
    },
    // never blocks, either document can go alone, so what is missing is only checked on the Send tab
    onNext: function () {
      let nextTab = this.tabIndex + 1
      this.saveChecklist('next').then(saved => {
        if (!saved) return false
        // Next on the contract tab saves and confirms the placements in one step
        if (this.tabIndex === 1 && this.activePowerContract && !this.powerContractConfirmed) {
          if (this.powerContractSignatureCount === 0) {
            this.failedToast('Add at least one customer signature box to the Power Contract.')
            return false
          }
          return this.onSavePowerContractPlacements()
        }
        return true
      }).then(saved => {
        if (saved) {
          this.tabIndex = nextTab
        }
      })
    },
    onPreview: function () {
      // Craig: each tab previews its own document, the Send tab the pack as it will be signed
      let part = this.tabIndex === 0 ? 'checklist' : (this.tabIndex === 1 ? 'contract' : '')
      if (part === 'contract' && !this.activePowerContract) {
        this.failedToast('Upload the Power Contract to preview it.')
        return
      }
      if (part === '' && !this.includeChecklist && !this.packIncludesContract) {
        this.failedToast('Include the delivery checklist or the Power Contract to preview them.')
        return
      }
      this.saveChecklist('preview').then(saved => {
        if (!saved) return false
        // dragged boxes have to be saved first or the preview would show them where they were
        if (this.activePowerContract && this.powerContractLocalDirty) {
          if (this.powerContractSignatureCount === 0) {
            this.failedToast('Add at least one customer signature box to the Power Contract.')
            return false
          }
          return this.onSavePowerContractPlacements()
        }
        return true
      }).then(saved => {
        if (!saved) return
        // as they will be signed, the customer lines carry the typed /S/ when the digital signature is on,
        // otherwise a "Customer signature" placeholder marks where they sign
        let name = part === 'checklist' ? 'Delivery Checklist' : (part === 'contract' ? 'Power Contract' : this.packTitle)
        this.deliveryChecklistDocument = {
          name: name,
          src: process.env.VUE_APP_ROOT_DOC_SITE + 'docs/deals/delivery-checklist/' + this.currentDeal.id + '/' + this.currentUser.id + '/false?preview=true&typedCustomerSignature=' + this.customerTypedSignature + (part ? '&part=' + part : ''),
          fileName: (part === 'contract' || (part === '' && !this.includeChecklist) ? 'power_contract_' : 'delivery_checklist_') + this.currentDeal.id + '.pdf',
          key: this.$uuid.v4()
        }
        // only the pack preview is what gets signed
        if (part === '') {
          this.previewViewed = true
        }
      })
    },
    onPreviewClosed: function () {
      if (this.deliveryChecklistDocument && this.deliveryChecklistDocument.downloadUrl) {
        URL.revokeObjectURL(this.deliveryChecklistDocument.src)
      }
      this.deliveryChecklistDocument = null
    },
    onSignNow: function () {
      if (!this.canSignOrSend) return
      this.saveChecklist('sign').then(saved => {
        if (saved) {
          this.isSignatureActive = true
        }
      })
    },
    onSign () {
      if (this.$refs.signature.isEmpty()) {
        this.failedToast('Please capture the customer signature before accepting.')
        return
      }
      this.loadingSign = true
      var png = this.$refs.signature.save()

      var signModel = {
        dealId: this.currentDeal.id,
        userId: this.currentUser.id,
        signatureString: png
      }

      dealService.signDeliveryChecklist(signModel).then(response => {
        this.loadingSign = false
        if (response.result === false || response.errors) {
          this.failedToast('Oops! Something went wrong')
          this.serverErrors = response.errors || []
          this.serverErrorMessage = response.message || ''
          this.isSignatureActive = false
        } else {
          this.isSignatureActive = false
          this.onChecklistSigned()
        }
      }).catch((error) => {
        this.loadingSign = false
        this.isSignatureActive = false
        this.serverErrorMessage = error.message
      })
    },
    onChecklistSigned: function () {
      this.deliveryChecklistControlActive = false
      let packUrl = this.signedCopiesUrl
      let packName = 'Signed ' + this.packTitle
      let fileName = 'delivery_pack_' + this.currentDeal.id + '.pdf'
      this.successToast('Success! ' + this.packTitle + ' signed.')
      this.onDealChanged()
      // the pack needs the login cookie, which the PDF viewer's own request does not send
      fetch(packUrl, { credentials: 'include' }).then(response => {
        if (!response.ok) throw new Error('Unexpected server response (' + response.status + ')')
        return response.blob()
      }).then(pack => {
        this.deliveryChecklistDocument = {
          name: packName,
          src: URL.createObjectURL(pack),
          downloadUrl: packUrl,
          fileName: fileName,
          key: this.$uuid.v4()
        }
      }).catch(() => {
        this.failedToast('The signed copy could not be shown. Use Download Last Signed.')
      })
    },
    onSignTyped: function () {
      if (!this.canSignOrSend) return
      this.saveChecklist('sign').then(saved => {
        if (!saved) return
        this.isSaving = true
        this.pendingAction = 'sign'
        dealService.signDeliveryChecklist({ dealId: this.currentDeal.id, userId: this.currentUser.id, typedSignature: true }).then(response => {
          this.isSaving = false
          this.confirmTypedSignature = false
          if (response.result === false || response.errors) {
            this.failedToast('Oops! Something went wrong')
            this.serverErrors = response.errors || []
            this.serverErrorMessage = response.message || ''
          } else {
            this.onChecklistSigned()
          }
        }).catch((error) => {
          this.isSaving = false
          this.confirmTypedSignature = false
          this.failedToast('Oops! Something went wrong')
          this.serverErrorMessage = error.message
        })
      })
    },
    onSignOnIpad: function () {
      if (!this.canSignOrSend) return
      this.saveChecklist('ipad').then(saved => {
        if (!saved) return
        this.isSaving = true
        this.pendingAction = 'ipad'
        documentSigningLinkService.create({ type: 'DeliveryChecklist', entityId: this.currentDeal.id }).then(response => {
          this.isSaving = false
          if (response.result === false || response.errors) {
            this.failedToast('Oops! Something went wrong')
            this.serverErrors = response.errors || []
            this.serverErrorMessage = response.message || ''
            return
          }
          this.ipadLink = {
            url: documentSigningLinkService.signingUrl(response.token),
            expiresAt: response.expiresAt,
            // the link dies on the server once the saved checklist changes, see EnsureDeliveryChecklistSigningLinkUnused
            checklistSnapshot: JSON.stringify(this.buildChecklistModel())
          }
        }).catch((error) => {
          this.isSaving = false
          this.failedToast('Oops! Something went wrong')
          this.serverErrorMessage = error.message
        })
      })
    },
    onIpadLinkInvalidated: function () {
      this.ipadLink = null
      this.failedToast('The checklist changed so the signing link no longer works, click Sign on iPad for a new one.')
    },
    onCloseIpadLink: function () {
      this.ipadLink = null
    },
    onDeliveryChecklistSignedRemotely: function (packet) {
      let data = packet && packet.data ? packet.data : null
      if (!data || data.type !== 'DeliveryChecklist' || !this.currentDeal || data.entityId !== this.currentDeal.id || !this.ipadLink) return
      this.ipadLink = null
      this.onChecklistSigned()
    },
    onClearSign () {
      this.$refs.signature.clear()
    },
    onCancelSignNow () {
      this.isSignatureActive = false
      this.loadingSign = false
    },
    onValidateSend: function () {
      if (!this.canSignOrSend) return
      this.validateBeforeSubmitWithCallBack(this.onSend)
    },
    onSend: function () {
      this.saveChecklist('send').then(saved => {
        if (!saved) return
        var emailAddresses = this.selectedRecipients.map(e => e.email).join(', ')
        var mobileNumbers = this.selectedRecipientsMobile.map(e => e.mobile).join(',')
        var deliveryChecklistSendModel = {
          subscriberId: this.currentDeal.subscriberId,
          dealerId: this.currentDeal.dealerId,
          dealId: this.currentDeal.id,
          type: 'DeliveryChecklist',
          destinationAddresses: emailAddresses,
          destinationMobileNumbers: mobileNumbers,
          overridingSubject: this.overridingSubject,
          optionalMessage: this.optionalMessage,
          attachDocuments: false,
          watermarkTitle: false,
          saleDate: null
        }

        this.pendingAction = 'send'
        this.isSaving = true
        dealService.sendDeliveryChecklistDocument(deliveryChecklistSendModel).then(response => {
          this.isSaving = false
          if (response.result === false || response.errors) {
            this.failedToast('Oops! Something went wrong')
            this.serverErrors = response.errors || []
            this.serverErrorMessage = response.message || ''
          } else {
            this.successToast('Success! Request created.')
            this.deliveryChecklistControlActive = false
            this.resetModal()
            this.onDealChanged()
          }
        }).catch((error) => {
          this.failedToast('Oops! Something went wrong')
          this.isSaving = false
          this.serverErrorMessage = error.message
        })
      })
    },
    onDealChanged: function () {
      this.hasSavedChanges = false
      this.$emit('on-save')
      eventBus.$emit('reload-deal', {})
    },
    setDealRecipientInformation: function () {
      if (this.currentDeal) {
        this.selectedRecipientCustomer = {
          id: this.currentDeal.purchaserId,
          fullName: this.purchaserDisplayName,
          email: this.currentDeal.purchaserContactEmail,
          mobile: this.currentDeal.purchaserContactMobile
        }
      }
    },
    onRecipientAdded: function (data) {
      var recipient = {
        fullName: data,
        email: data
      }
      this.selectedRecipients.push(recipient)
    },
    getMessagePreface: function () {
      let result = ''
      if (this.selectedRecipientCustomer && this.selectedRecipientCustomer.fullName) {
        result += 'Hi ' + this.selectedRecipientCustomer.fullName + ',\n\n'
      }
      result += 'Please review and sign the attached ' + this.packTitle + ' for vehicle ' + this.vehicleName + (this.inventoryItem ? ' - ' + this.inventoryItem.vinNo : '') + '.' + '\n'
      if (this.currentUser) {
        result += '\nThank you, \n' + this.currentUser.fullName
      }
      return result
    },
    resetModal: function () {
      this.selectedRecipientCustomer = null
      this.selectedRecipients = []
      this.selectedRecipientsMobile = []
      this.optionalMessage = ''
      this.pendingAction = ''
    },
    onCancel: function () {
      this.deliveryChecklistControlActive = false
      this.resetModal()
      // refresh the deal only once the modal is closed so the saved checklist and status are picked up
      if (this.hasSavedChanges) {
        this.onDealChanged()
      }
    },
    onShowReminderModal: function () {
      this.showConfirmReminder = true
    },
    onHideReminderModal: function () {
      this.showConfirmReminder = false
      this.signatureRequest = null
    },
    sendReminder: function () {
      if (!this.sendingReminder) {
        this.sendingReminder = true
        documentSigningRequestService.sendReminder(this.signatureRequest).then(response => {
          if (response.errors) {
            this.serverErrors = response.errors
          } else {
            this.signatureRequest.reminderSent = true
            this.successToast('Signature request reminder has been sent.')
          }
        }).catch((error) => {
          throw error
        }).finally(() => {
          this.onHideReminderModal()
          this.sendingReminder = false
        })
      }
    },
    onShowCancellationRequestModal: function () {
      this.showCancelRequest = true
    },
    onHideCancellationRequestModal: function () {
      this.showCancelRequest = false
      this.cancelSignatureRequest = null
    },
    sendCancellationRequest: function () {
      if (!this.sendingCancellationRequest) {
        this.sendingCancellationRequest = true
        let id = this.cancelSignatureRequest.signatureRequestId !== null && this.cancelSignatureRequest.signatureRequestId !== '' ? this.cancelSignatureRequest.signatureRequestId : this.cancelSignatureRequest.id
        documentSigningRequestService.cancelSignatureRequest(id).then(response => {
          if (response.errors) {
            this.serverErrors = response.errors
          } else {
            this.cancelSignatureRequest.cancellationSent = true
            this.successToast('Signature cancellation request has been sent.')
          }
        }).catch((error) => {
          this.serverErrorMessage = error.message
        }).finally(() => {
          this.onHideCancellationRequestModal()
          this.sendingCancellationRequest = false
        })
      }
    },
    isCancellationDisabled: function (signatureRequest) {
      if (signatureRequest.sent === true && (signatureRequest.signingStatus === 0 || signatureRequest.signingStatus === 1 || signatureRequest.signingStatus === 2)) {
        return false
      }
      return true
    },
    isReminderDisabled: function (signatureRequest) {
      if (signatureRequest.sent === true && (signatureRequest.signingStatus === 0 || signatureRequest.signingStatus === 1 || signatureRequest.signingStatus === 2)) {
        return false
      }
      return true
    },
    convertTimeString: function (date, status) {
      if (status !== 0) {
        var d = new Date(date)
        var hour = '' + d.getHours()
        var minute = '' + d.getMinutes()

        if (hour.length < 2) hour = '0' + hour
        if (minute.length < 2) minute = '0' + minute

        return [hour, minute].join(':')
      } else {
        return ''
      }
    },
    onCancelSignatureRequest: function (signatureRequest) {
      if (signatureRequest.sent === true && (signatureRequest.signingStatus === 0 || signatureRequest.signingStatus === 1 || signatureRequest.signingStatus === 2)) {
        this.cancelSignatureRequest = signatureRequest
        this.onShowCancellationRequestModal()
      } else {
        this.failedToast('Signature request cannot be cancelled at this point.')
      }
    },
    onSignatureRequestReminder: function (signatureRequest) {
      if (signatureRequest.sent === true && (signatureRequest.signingStatus === 0 || signatureRequest.signingStatus === 1 || signatureRequest.signingStatus === 2)) {
        this.signatureRequest = signatureRequest
        this.onShowReminderModal()
      } else {
        this.failedToast('Signature request reminder cannot be sent at this point.')
      }
    },
    loadDeliveryChecklistSendHistory: function () {
      const searchCriteria = {
        dealId: this.currentDeal.id,
        dealerId: this.currentDealer.id,
        signingRequestType: 11,
        query: '',
        pageSize: this.perPage,
        sortBy: 'sentAt',
        page: this.page
      }
      this.isLoading = true
      documentSigningRequestService.searchByType(searchCriteria).then(response => {
        this.deliveryChecklistSendHistory = []
        let currentTotal = response.totalAvailable
        if (response.totalAvailable / this.perPage > 1000) {
          currentTotal = this.perPage * 1000
        }
        this.total = currentTotal
        response.results.forEach((item) => {
          item.reminderSent = false
          item.cancellationSent = false
          this.deliveryChecklistSendHistory.push(item)
        })
        this.isLoading = false
      }).catch((error) => {
        this.deliveryChecklistSendHistory = []
        this.total = 0
        this.isLoading = false
        this.serverErrorMessage = error.message
      })
    }
  },
  mounted: function () {
    this.selectedDealer = this.$store.state.dealer.currentDealer
    eventBus.$on('document-signed', this.onDeliveryChecklistSignedRemotely)
  },
  beforeDestroy: function () {
    eventBus.$off('document-signed', this.onDeliveryChecklistSignedRemotely)
  },
  watch: {
    customerTypedSignature: function () {
      this.confirmTypedSignature = false
    },
    // the subject and message name what is being sent, so they follow the Include choice
    packTitle: function (newValue, oldValue) {
      if (!this.deliveryChecklistControlActive || newValue === oldValue) return
      this.overridingSubject = newValue + ' - ' + this.vehicleName
      if (this.selectedRecipientCustomer) {
        this.optionalMessage = this.getMessagePreface()
      }
    },
    tabIndex: function (newValue, oldValue) {
      if (newValue === 1) {
        this.powerContractTabVisited = true
      }
      if (newValue === 3) {
        this.loadDeliveryChecklistSendHistory()
      }
    },
    powerContractKey: function (newValue, oldValue) {
      if (newValue !== oldValue) {
        this.initPowerContractFields()
      }
    },
    selectedRecipientCustomer: function (newValue, oldValue) {
      this.selectedRecipients = []
      this.selectedRecipientsMobile = []
      if (newValue) {
        if (newValue.email) {
          var recipient = {
            fullName: newValue.fullName,
            email: newValue.email
          }
          let existingContact = this.selectedRecipients.find(x => x.email === newValue.email)
          if (!existingContact) {
            this.selectedRecipients.push(recipient)
          }
        }
        if (newValue.mobile) {
          let recipientMobile = {
            mobile: newValue.mobile,
            fullName: newValue.fullName
          }
          let existingContact = this.selectedRecipientsMobile.find(x => x.mobile === newValue.mobile)
          if (!existingContact) {
            this.selectedRecipientsMobile.push(recipientMobile)
          }
        }
        this.optionalMessage = this.getMessagePreface()
      }
    }
  }
}
</script>
<style scoped>
  .delivery-checklist-modal {
    .modal-card-body {
      position:relative;
      max-height: calc(70vh - 10px) !important;
      height: calc(70vh - 10px) !important;
      max-width: 100% !important;
      min-width: 100% !important;
      overflow-y: auto !important;
      width: 960px !important;
    }
  }

  .checklist-section-title {
    font-weight: bold;
    border-bottom: 1px solid #dbdbdb;
    margin-bottom: 6px;
  }

  /* bulma's negative row margin pulls each row up over the one above once the column padding is trimmed */
  .checklist-item {
    margin-top: 0 !important;
    margin-bottom: 0 !important;
    .column {
      padding-top: 2px;
      padding-bottom: 2px;
    }
  }

  .power-contract-status {
    align-items: center;
  }

  /* keep the tab headers in view while the body scrolls, -12px offsets the body padding */
  .delivery-checklist-modal ::v-deep .b-tabs {
    background: inherit;
  }

  .delivery-checklist-modal ::v-deep .b-tabs > nav.tabs {
    position: sticky;
    top: -12px;
    z-index: 5;
    background: inherit;
    padding-top: 12px;
    margin-top: -12px;
  }

  /* the required star on mileage makes its label taller, so fix the label height to keep the four inputs level */
  .delivery-header-fields .label {
    height: 1.5em;
    line-height: 1.5em;
    white-space: nowrap;
  }

  .delivery-header-fields .label ::v-deep .icon {
    height: 1em;
    vertical-align: top;
  }

  /* size the element-ui date picker like the bulma inputs beside it */
  .delivery-date-control ::v-deep .el-date-editor.el-input {
    width: 100% !important;
  }

  .delivery-date-control ::v-deep .el-input__inner {
    height: 2.25em;
    line-height: 2.25em;
    font-size: 1rem;
  }

  .footer-preview {
    margin-left: auto;
  }

  .modal-sign-body {
    position:relative;
    overflow-y: auto;
    background: white;
  }

  @media screen and (max-height: 600px), print {
    .delivery-checklist-modal {
      .modal-card-body {
        max-height: calc(70vh - 60px) !important;
        height: calc(70vh - 60px) !important;
      }
    }
  }
</style>
